import { useState, useEffect, useMemo } from 'react';
import { useMerryGoRound, useCreateMerryGoRound, useUpdateMerryGoRound } from '../hooks/useMerryGoRound.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { Pencil, Save, ArrowUp, ArrowDown, X } from 'lucide-react';
import {useIsAdmin} from "../hooks/useIsAdmin.ts";
import { PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const FREQUENCY_TYPES = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Custom', value: 'custom' },
];
const FREQUENCY_UNITS = [
  { label: 'Days', value: 'days' },
  { label: 'Weeks', value: 'weeks' },
  { label: 'Months', value: 'months' },
];
const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

export default function MerryGoRoundPage() {
  const { data, isLoading } = useMerryGoRound();
  const { data: membersData } = useMembers();
  const { data: profile } = useGetProfile();
  const createMGR = useCreateMerryGoRound();
  const updateMGR = useUpdateMerryGoRound();
  const mgr = useMemo(() => data?.data || {}, [data]);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    start_date: '',
    end_date: '',
    is_perpetual: true,
    total_rounds: '',
    amount: '',
    frequency_type: 'weekly',
    frequency_interval: 1,
    frequency_unit: 'weeks',
    contribution_day: '',
    recipients_per_round: 1,
    members: [],
  });
  const [formError, setFormError] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [showAllMembers, setShowAllMembers] = useState(false);

  const isAdmin = useIsAdmin();
  const { can } = usePermissions();

  // Update form when merry-go-round data is loaded
  useEffect(() => {
    if (mgr.id) {
      console.log(mgr.members, "mgr members");
      setForm({
        start_date: mgr.start_date || '',
        end_date: mgr.end_date || '',
        is_perpetual: mgr.is_perpetual ?? true,
        total_rounds: mgr.total_rounds || 0,
        amount: mgr.amount || '',
        frequency_type: mgr.frequency_type || 'weekly',
        frequency_interval: mgr.frequency_interval || 1,
        frequency_unit: mgr.frequency_unit || 'weeks',
        contribution_day: mgr.contribution_day || '',
        recipients_per_round: mgr.recipients_per_round || 1,
        members: mgr.members?.map((m, i) => ({ id: m.member?.id, position: m.position ?? i + 1 })) || [],
      });
    }
  }, [mgr]);

  const allMembers = membersData?.data || [];
  const filteredMembers = showAllMembers
    ? allMembers
    : allMembers.filter(m => m.name.toLowerCase().includes(memberSearch.toLowerCase()) || m.phone_number.includes(memberSearch));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleMemberToggle = (memberId) => {
    setForm(f => {
      const exists = f.members.some(m => m.id === memberId);
      let newMembers;
      if (exists) {
        newMembers = f.members.filter(m => m.id !== memberId);
      } else {
        newMembers = [...f.members, { id: memberId, position: f.members.length + 1 }];
      }
      return { ...f, members: newMembers };
    });
  };

  const moveMember = (fromIndex, toIndex) => {
    setForm(f => {
      const newMembers = [...f.members];
      const [moved] = newMembers.splice(fromIndex, 1);
      newMembers.splice(toIndex, 0, moved);
      // Update positions starting from 1
      return { ...f, members: newMembers.map((m, i) => ({ ...m, position: i + 1 })) };
    });
  };

  const validateForm = () => {
    if (!form.start_date) return 'Start date is required';
    if (!form.is_perpetual && !form.end_date) return 'End date is required when not perpetual';
    if (!form.is_perpetual && new Date(form.end_date) <= new Date(form.start_date)) return 'End date must be after start date';
    if (!form.amount || Number(form.amount) <= 0) return 'Amount per round is required and must be > 0';
    if (!form.frequency_type) return 'Frequency type is required';
    if (form.frequency_type === 'custom' && !form.frequency_unit) return 'Frequency unit is required for custom frequency';
    if (!form.frequency_interval || Number(form.frequency_interval) < 1) return 'Frequency interval is required and must be at least 1';
    if (!form.contribution_day) return 'Contribution day is required';
    if (!form.recipients_per_round || Number(form.recipients_per_round) < 1) return 'Recipients per round is required and must be at least 1';
    if (!form.members || form.members.length === 0) return 'At least one member is required';
    if (form.members.some(m => m.position === undefined || m.position === null)) return 'Each member must have a position';
    if (form.recipients_per_round > form.members.length) return 'Number of recipients cannot be greater than total members';
    return '';
  };

  const handleSave = () => {
    const err = validateForm();
    if (err) {
      setFormError(err);
      return;
    }
    setFormError('');
    const payload = { ...form };
    payload.members = [...form.members].sort((a, b) => a.position - b.position);
    if (mgr.id) {
      updateMGR.mutate({ id: mgr.id, ...payload }, { onSuccess: () => setIsEditing(false) });
    } else {
      createMGR.mutate(payload, { onSuccess: () => setIsEditing(false) });
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    
      <div className="max-w-3xl mx-auto py-8">
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Merry Go Round</h2>
          <PermissionGate permission={PERMISSIONS.EDIT_GROUP_ROTATION}>
            {!isEditing && (
              <button className="flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700" onClick={() => setIsEditing(true)}>
                <Pencil size={16} className="mr-1" /> Edit
              </button>
            )}
          </PermissionGate>
        </div>
        {formError && <div className="text-red-600 mb-2">{formError}</div>}
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Contribution Amount</label>
              <div className="text-gray-500 text-sm mb-2">How much will each member contribute in each round?</div>
              <input type="number" name="amount" min={0} className="w-full border rounded px-3 py-2" value={form.amount} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contribution Schedule</label>
              <div className="text-gray-500 text-sm mb-2">How often will members make their contributions?</div>
              <select name="frequency_type" className="w-full border rounded px-3 py-2" value={form.frequency_type} onChange={handleChange}>
                {FREQUENCY_TYPES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              {form.frequency_type === 'custom' && (
                <div className="flex gap-2 mt-2">
                  <input type="number" name="frequency_interval" min={1} className="border rounded px-3 py-2 w-32" value={form.frequency_interval} onChange={handleChange} />
                  <select name="frequency_unit" className="border rounded px-3 py-2 w-32" value={form.frequency_unit} onChange={handleChange}>
                    {FREQUENCY_UNITS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              )}
            </div>
            {form.frequency_type !== 'daily' && (
              <div>
                <label className="block text-sm font-medium mb-1">{form.frequency_type === 'weekly' ? 'Day of Week' : 'Day of Month'}</label>
                <div className="text-gray-500 text-sm mb-2">
                  {form.frequency_type === 'weekly'
                    ? 'Which day of the week will contributions be made?'
                    : 'Which day of the month will contributions be made?'}
                </div>
                {form.frequency_type === 'monthly' ? (
                  <select name="contribution_day" className="w-full border rounded px-3 py-2" value={form.contribution_day} onChange={handleChange} required>
                    <option value="">Select day of month</option>
                    {MONTH_DAYS.map(day => <option key={day} value={day}>{day}</option>)}
                  </select>
                ) : (
                  <select name="contribution_day" className="w-full border rounded px-3 py-2" value={form.contribution_day} onChange={handleChange} required>
                    <option value="">Select day of week</option>
                    {WEEKDAYS.map(day => <option key={day} value={day}>{day}</option>)}
                  </select>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <div className="text-gray-500 text-sm mb-2">When will the first contribution be made?</div>
              <input type="date" name="start_date" className="w-full border rounded px-3 py-2" value={form.start_date?.substring(0,10) || ''} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Round Duration</label>
              <div className="text-gray-500 text-sm mb-2">Will this merry-go-round continue indefinitely or have a specific number of rounds?</div>
              <div className="flex items-center gap-2">
                <span>No End Date</span>
                <input type="checkbox" name="is_perpetual" checked={form.is_perpetual} onChange={handleChange} className="ml-2" />
              </div>
            </div>
            {!form.is_perpetual && (
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <div className="text-gray-500 text-sm mb-2">When will this merry-go-round end?</div>
                <input type="date" name="end_date" className="w-full border rounded px-3 py-2" value={form.end_date?.substring(0,10) || ''} onChange={handleChange} />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Number of rounds so far</label>
              <div className="text-gray-500 text-sm mb-2">How many rounds have been completed so far?</div>
              <input type="number" name="total_rounds" min={1} className="w-full border rounded px-3 py-2" value={form.total_rounds} onChange={handleChange} />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Select Members</label>
              <div className="text-gray-500 text-sm mb-2">Choose the members who will participate in this merry-go-round</div>
              <div className="flex items-center gap-2 mb-2">
                <span>All Members</span>
                <input type="checkbox" checked={showAllMembers} onChange={e => setShowAllMembers(e.target.checked)} />
              </div>
              {!showAllMembers && (
                <input
                  type="text"
                  className="border rounded px-3 py-2 mb-2 w-full"
                  placeholder="Type member name or phone..."
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                />
              )}
              <div className="flex flex-wrap gap-2 mb-2">
                {filteredMembers.map(m => (
                  <div key={m.id} className={`px-3 py-1 rounded border cursor-pointer ${form.members.some(mem => mem.id === m.id) ? 'bg-blue-100 border-blue-400' : 'bg-gray-100 border-gray-300'}`}
                    onClick={() => handleMemberToggle(m.id)}
                  >
                    {m.name} ({m.phone_number})
                  </div>
                ))}
              </div>
              {/* Selected Members List */}
              {form.members.length > 0 && (
                <div className="mt-2">
                  <div className="text-sm text-gray-600 mb-2">Selected Members (drag to reorder)</div>
                  {form.members.map((m, idx) => {
                    const member = allMembers.find(mem => mem.id === m.id);
                    return (
                      <div key={m.id} className="flex items-center gap-2 bg-gray-50 p-2 rounded mb-1">
                        <span className="w-6 text-gray-800">{idx + 1}.</span>
                        <span className="flex-1 truncate">{member?.name || m.id}</span>
                        {idx > 0 && <button type="button" onClick={() => moveMember(idx, idx - 1)} className="p-1"><ArrowUp size={16} /></button>}
                        {idx < form.members.length - 1 && <button type="button" onClick={() => moveMember(idx, idx + 1)} className="p-1"><ArrowDown size={16} /></button>}
                        <button type="button" onClick={() => handleMemberToggle(m.id)} className="p-1"><X size={16} /></button>
                      </div>
                    );
                  })}
                </div>
              )}
              {form.members.length === 0 && <div className="text-red-500 text-xs mt-1">Please select at least one member</div>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Recipients per Round</label>
              <div className="text-gray-500 text-sm mb-2">How many members will receive money in each round? This allows multiple members to receive contributions simultaneously.</div>
              <input 
                type="number" 
                name="recipients_per_round" 
                min={1} 
                max={form.members.length || 1} 
                className="w-full border rounded px-3 py-2" 
                value={form.recipients_per_round} 
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value > form.members.length) {
                    setFormError(`Number of recipients cannot exceed total members (${form.members.length})`);
                    return;
                  }
                  handleChange(e);
                }} 
              />
              {form.members.length > 0 && (
                <div className="text-gray-500 text-sm mt-1">Maximum possible: {form.members.length} members</div>
              )}
              {form.recipients_per_round > 1 && (
                <div className="mt-2 bg-blue-50 p-3 rounded-lg text-blue-700 text-sm">
                  With {form.recipients_per_round} recipients per round, members will receive money in a rotating order. For example:<br />
                  • Round 1: First {form.recipients_per_round} members<br />
                  • Round 2: Next {form.recipients_per_round} members<br />
                  • And so on, cycling through all members
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={() => setIsEditing(false)} type="button">Cancel</button>
              <button className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 flex items-center" onClick={handleSave} type="button"><Save size={16} className="mr-1" />Save</button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div><span className="font-medium">Start Date:</span> {mgr.start_date ? new Date(mgr.start_date).toLocaleDateString() : '-'}</div>
            <div><span className="font-medium">Amount per Round:</span> Ksh {mgr.amount}</div>
            <div><span className="font-medium">Frequency:</span> {mgr.frequency_type} every {mgr.frequency_interval} {mgr.frequency_unit || ''}</div>
            <div><span className="font-medium">Contribution Day:</span> {mgr.contribution_day || '-'}</div>
            <div><span className="font-medium">Recipients per Round:</span> {mgr.recipients_per_round}</div>
            <div><span className="font-medium">Perpetual:</span> {mgr.is_perpetual ? 'Yes' : 'No'}</div>
            {!mgr.is_perpetual && <div><span className="font-medium">Total Rounds:</span> {mgr.total_rounds}</div>}
          </div>
        )}
      </div>
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Metrics</h3>
        <div className="flex flex-wrap gap-6">
          <div className="bg-blue-50 rounded-lg p-4 flex-1 min-w-[180px]">
            <div className="text-gray-500 text-sm">Total Members</div>
            <div className="text-2xl font-bold">{mgr.members?.length || 0}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 flex-1 min-w-[180px]">
            <div className="text-gray-500 text-sm">Total Rounds</div>
            <div className="text-2xl font-bold">{mgr.total_rounds || '-'}</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 flex-1 min-w-[180px]">
            <div className="text-gray-500 text-sm">Amount per Round</div>
            <div className="text-2xl font-bold">Ksh {mgr.amount || '-'}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h3 className="text-xl font-semibold mb-4">Next Round</h3>
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">What is a Round?</h4>
          <p className="text-blue-700 text-sm">
            A round is completed when all members have successfully contributed their share of money for a particular set date. 
            The recipients shown below will receive the total contributions from all members once the round is complete.
          </p>
          <div className="mt-2 text-sm text-blue-600">
            <p>• Each member contributes: Ksh {mgr.amount || 0}</p>
            <p>• Total per round: Ksh {(mgr.amount || 0) * (mgr.members?.length || 0)}</p>
            <p>• Each recipient receives: Ksh {mgr.amount ? Math.floor((mgr.amount * (mgr.members?.length || 0)) / (mgr.recipients_per_round || 1)) : 0}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-gray-500 text-sm mb-2">Next Recipients</div>
            {mgr.next_round_recipients?.length > 0 ? (
              <div className="space-y-2">
                {mgr.next_round_recipients.map((m, i) => (
                  <div key={m.member?.id} className="flex items-center gap-2">
                    <span className="text-gray-600">{m.position ?? i + 1}.</span>
                    <span className="font-medium">{m.member?.name}</span>
                    <span className="text-gray-500">({m.member?.phone_number})</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500">No recipients for next round</div>
            )}
          </div>
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="text-gray-500 text-sm mb-2">Next Round Date</div>
            {mgr.next_round ? (
              <div className="space-y-2">
                <div className="text-lg font-medium">
                  {new Date(mgr.next_round).toLocaleDateString()}
                </div>
                <div className="text-sm text-gray-600">
                  {mgr.frequency_type === 'weekly' && 'Every ' + WEEKDAYS[parseInt(mgr.contribution_day)]}
                  {mgr.frequency_type === 'monthly' && `Day ${mgr.contribution_day} of each month`}
                  {mgr.frequency_type === 'daily' && 'Every day'}
                  {mgr.frequency_type === 'custom' && `Every ${mgr.frequency_interval} ${mgr.frequency_unit}`}
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  <p>• All members must contribute by this date</p>
                  <p>• Recipients will receive their share after all contributions are collected</p>
                </div>
              </div>
            ) : (
              <div className="text-gray-500">No next round scheduled</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Members</h3>
        <div className="overflow-x-auto">
          <table className="responsive-table min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Received</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[...(mgr.members || [])].sort((a, b) => a.position - b.position).map((m) => (
                <tr key={m.member?.id ?? m.position}>
                  <td data-label="#" className="px-4 py-2">{m.position}</td>
                  <td data-label="Name" className="px-4 py-2">{m.member?.name || '-'}</td>
                  <td data-label="Phone" className="px-4 py-2">{m.member?.phone_number || '-'}</td>
                  <td data-label="Last Received" className="px-4 py-2">{m.last_received ? new Date(m.last_received).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    
  );
} 